/**
 * Vue directives for declarative analytics control.
 *
 * v-track-ignore  — opt an element and its subtree out of auto-tracking
 * v-track         — override the tracked event name and/or inject extra properties
 *
 * Both directives write data attributes that the global click listener reads,
 * so no Vue component lifecycle coupling is needed.
 */

import type { ObjectDirective } from 'vue'
import { AnalyticsEvents } from '../types'
import type { AnalyticsEventProperties } from '../types/properties'

/**
 * Shape of the v-track binding value.
 * `event` overrides the default `{snake_case_element_id}_clicked` name.
 * All other keys are merged into the tracked payload as extra properties.
 */
export interface AnalyticsOverride {
  event?: string
  [key: string]: unknown
}

export function trackIgnoreDatasetProps(): Record<'data-track-ignore', ''> {
  return { 'data-track-ignore': '' }
}

export function trackBindingDatasetProps(value?: AnalyticsOverride | null): Record<'data-analytics', string> | Record<string, never> {
  if (!value) return {}
  return { 'data-analytics': JSON.stringify(value) }
}

function applyTrackBindingToElement(el: HTMLElement, value?: AnalyticsOverride | null): void {
  if (value) {
    el.dataset.analytics = JSON.stringify(value)
  } else {
    delete el.dataset.analytics
  }
}

interface AutoTrackedClick {
  event: string
  properties: AnalyticsEventProperties
}

/**
 * HighRise buttons wrap their slot content in `<p id="${id}-label">`, so a click on the label text
 * resolves to that inner id rather than the id the caller authored. Prefer the owning element's id when
 * the match is exactly that pattern, so `element` / `context` describe the control rather than its
 * label node.
 */
const LABEL_ID_SUFFIX = '-label'

function resolveOwningElementId(elementWithId: HTMLElement): string {
  const id = elementWithId.id
  if (!id.endsWith(LABEL_ID_SUFFIX)) return id

  const ownerId = id.slice(0, -LABEL_ID_SUFFIX.length)
  const owner = elementWithId.parentElement?.closest('[id]') as HTMLElement | null
  return owner?.id === ownerId ? ownerId : id
}

/** Convention kebab id → GA event name when `v-track.event` is absent. */
function elementIdClickEventName(elementId: string): string {
  return `${elementId.replace(/-/g, '_')}_clicked`
}

function resolveTrackedElementId(target: HTMLElement): string | undefined {
  const elementWithId = target.closest('[id]') as HTMLElement | null
  return elementWithId?.id ? resolveOwningElementId(elementWithId) : undefined
}

function parseAnalyticsOverride(target: HTMLElement): AnalyticsOverride {
  const overrideEl = target.closest('[data-analytics]') as HTMLElement | null
  const raw = overrideEl?.dataset?.analytics
  if (!raw) return {}
  try {
    return JSON.parse(raw) as AnalyticsOverride
  } catch {
    return {}
  }
}

function idDimensionProps(parts: string[]): Pick<AnalyticsEventProperties, 'page' | 'context' | 'element'> {
  if (parts.length >= 3) {
    return {
      page: parts[0],
      context: parts.slice(1, -1).join('-'),
      element: parts.at(-1),
    }
  }
  if (parts.length >= 1) return { page: parts[0] }
  return {}
}

function defaultAutoClickEvent(id: string | undefined, hasConventionId: boolean): string {
  return hasConventionId && id ? elementIdClickEventName(id) : AnalyticsEvents.ELEMENT_CLICKED
}

export const getAutoTrackedClick = (target: HTMLElement): AutoTrackedClick | null => {
  if (target.closest('[data-track-ignore]')) return null

  const id = resolveTrackedElementId(target)
  const parts = id?.split('-') ?? []
  const hasConventionId = parts.length >= 3
  const { event, ...extraProps } = parseAnalyticsOverride(target)

  return {
    event: event ?? defaultAutoClickEvent(id, hasConventionId),
    properties: {
      ...(id ? { elementId: id } : {}),
      elementType: target.tagName.toLowerCase(),
      elementText: target.textContent?.trim().substring(0, 100) || undefined,
      ...idDimensionProps(parts),
      extra: extraProps,
    },
  }
}

/**
 * Native controls turn Enter/Space into a synthesised click event, so the click path already covers
 * them. Anything else — a `role="button"` span, a `role="menuitem"` div — gets no such click, because
 * ARIA grants semantics, not behaviour. Without this path those controls report nothing at all for
 * keyboard and screen-reader users, which silently biases every click metric toward pointer users.
 */
const NATIVE_ACTIVATION_SELECTOR = 'button, a[href], input, select, textarea, summary, [contenteditable="true"]'

export const SPACE_KEY = ' '

/**
 * Activation keys per role, from the WAI-ARIA Authoring Practices keyboard interaction tables.
 *
 * Space is not a universal activation key. `role="link"` activates on Enter only — a custom link that
 * reported on Space recorded an activation the control never performs. Conversely `role="checkbox"`,
 * `role="switch"` and `role="radio"` are Space-only. Keying off the role rather than one shared
 * key set is what keeps the recorded event and the performed action the same thing.
 */
const ROLE_ACTIVATION_KEYS: Readonly<Record<string, readonly string[]>> = {
  button: ['Enter', SPACE_KEY],
  menuitem: ['Enter', SPACE_KEY],
  menuitemcheckbox: ['Enter', SPACE_KEY],
  menuitemradio: ['Enter', SPACE_KEY],
  tab: ['Enter', SPACE_KEY],
  option: ['Enter', SPACE_KEY],
  link: ['Enter'],
  switch: [SPACE_KEY],
  checkbox: [SPACE_KEY],
  radio: [SPACE_KEY],
}

/** Derived from the key table so the two can never drift apart. */
const KEYBOARD_ACTIVATABLE_ROLE_SELECTOR = Object.keys(ROLE_ACTIVATION_KEYS)
  .map(role => `[role="${role}"]`)
  .join(', ')

/**
 * True when the key press alone is not proof that the control acted on it.
 *
 * A control that genuinely handles Space must call `preventDefault()`, otherwise the page scrolls
 * underneath the user — so `defaultPrevented` is a real activation signal for Space. Enter carries no
 * such obligation, so an Enter press on a role-only control with no keyboard handler at all stays
 * indistinguishable from a real activation and is still counted.
 *
 * `defaultPrevented` is only final once the event has finished dispatching, so callers must read it
 * after the current task rather than inside a capture-phase listener.
 */
export const keyboardActivationNeedsProof = (event: KeyboardEvent): boolean => event.key === SPACE_KEY

export const getKeyboardActivatedClick = (event: KeyboardEvent): AutoTrackedClick | null => {
  // A held key repeats; the user activated the control once.
  if (event.repeat) return null

  const target = event.target as HTMLElement | null
  if (!target?.closest) return null

  if (target.closest(NATIVE_ACTIVATION_SELECTOR)) return null
  if (target.isContentEditable) return null

  const control = target.closest(KEYBOARD_ACTIVATABLE_ROLE_SELECTOR) as HTMLElement | null
  const activationKeys = ROLE_ACTIVATION_KEYS[control?.getAttribute('role') ?? '']
  if (!activationKeys?.includes(event.key)) return null

  // `pointer-events: none` blocks the click path but not keydown, so a disabled ARIA control would
  // otherwise report keyboard activations it never acts on.
  if (target.closest('[aria-disabled="true"], [data-loading="true"]')) return null

  return getAutoTrackedClick(target)
}

/**
 * v-track-ignore
 * Marks an element (and its descendants) as opted-out of auto-click tracking.
 *
 * @example
 * <button v-track-ignore @click="doSomething">Not tracked</button>
 */
export const vTrackIgnore: ObjectDirective<HTMLElement> = {
  getSSRProps() {
    return trackIgnoreDatasetProps()
  },
  mounted(el) {
    el.dataset.trackIgnore = ''
  },
}

/**
 * v-track
 * Overrides the event name and/or injects extra properties into the auto-tracked
 * click payload. Convention ids (≥3 hyphen parts) derive `{snake_id}_clicked`;
 * short ids and clicks with no id fall back to `element_clicked`.
 *
 * @example
 * <!-- Override event name + add a property -->
 * <div id="courses-hero-cta" v-track="{ event: 'hero_cta_clicked', campaign: 'spring' }">
 *
 * <!-- Add a property only (event name stays courses_hero_cta_clicked) -->
 * <div id="courses-hero-cta" v-track="{ placement: 'above_fold' }">
 *
 * A `null` value removes the override, which is how a control that is only sometimes the tracked
 * action — a submit button shared by create and edit — stops claiming the creation event.
 */
export const vTrack: ObjectDirective<HTMLElement, AnalyticsOverride | null> = {
  getSSRProps(binding) {
    return trackBindingDatasetProps(binding.value)
  },
  mounted(el, binding) {
    applyTrackBindingToElement(el, binding.value)
  },
  updated(el, binding) {
    applyTrackBindingToElement(el, binding.value)
  },
}
